<template>
  <button
    type="button"
    @click="!locked && openModal()"
    @mouseenter="!locked && (showCameraIcon = true)"
    @mouseleave="showCameraIcon = false"
    :title="locked ? lockedMessage : undefined"
    :class="locked ? 'cursor-not-allowed' : ''"
    class="relative focus:outline-none group"
  >
    <template v-if="pictureUrl">
      <img :src="pictureUrl" :alt="avatarName"
        :class="[shapeClass, size]"
        class="object-cover ring-4 ring-white dark:ring-[#1C2128] shadow-lg" />
    </template>
    <template v-else>
      <div :class="[avatarColorClass || 'bg-gradient-to-br from-blue-500 to-blue-700', shapeClass, size]"
        class="ring-4 ring-white dark:ring-[#1C2128] shadow-lg flex items-center justify-center">
        <span class="font-bold text-white tracking-tight select-none" :class="avatarTextSize">{{ avatarName }}</span>
      </div>
    </template>

    <Transition name="fade">
      <div v-if="showCameraIcon && !locked" :class="shapeClass" class="absolute inset-0 bg-black/50 flex items-center justify-center">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"/>
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"/>
        </svg>
      </div>
    </Transition>
  </button>

  <Teleport to="body">
    <Transition name="modal">
      <div v-if="showModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="closeModal">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-[#1C2128] rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 w-full max-w-md animate-in overflow-hidden">

          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ title }}</h3>
            <button @click="closeModal"
              class="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="px-6 py-5">
            <!-- A new file was just selected: interactive crop, not the plain preview -->
            <div v-if="cropping" class="flex flex-col items-center gap-3">
              <ImageCropper ref="cropperRef" :image-src="selectedFileUrl" :shape="shape" />
              <button type="button" @click="triggerFileInput"
                class="text-xs font-medium text-[#0070F2] hover:text-[#0058C7] transition-colors">
                Choose a different photo
              </button>
            </div>

            <!-- Idle, or a removal is pending -->
            <div v-else class="flex items-center gap-5">
              <div class="flex-shrink-0">
                <template v-if="tempPreview">
                  <img :src="tempPreview" :class="shapeClass" class="w-20 h-20 object-cover ring-1 ring-gray-200 dark:ring-gray-700"/>
                </template>
                <template v-else>
                  <div :class="[avatarColorClass || 'bg-[#0070F2]', shapeClass]"
                    class="w-20 h-20 ring-1 ring-gray-200 dark:ring-gray-700 flex items-center justify-center">
                    <span class="text-xl font-bold text-white">{{ avatarName }}</span>
                  </div>
                </template>
              </div>
              <div class="flex flex-col gap-2 flex-1 min-w-0">
                <label :for="uploadInputId"
                  class="inline-flex items-center justify-center gap-2 px-3.5 py-2 bg-[#0070F2] hover:bg-[#0058C7] text-white text-sm font-medium rounded-md cursor-pointer transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
                  </svg>
                  Upload picture
                </label>
                <button @click="removePicture"
                  class="inline-flex items-center justify-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                  </svg>
                  Remove picture
                </button>
                <button v-if="defaultPictureUrl && tempPreview !== defaultPictureUrl" @click="useDefaultPicture"
                  class="inline-flex items-center justify-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md transition-colors">
                  <img :src="defaultPictureUrl" alt="" class="w-4 h-4 rounded object-contain flex-shrink-0" />
                  Set default icon
                </button>
              </div>
            </div>

            <input :id="uploadInputId" ref="fileInputEl" type="file" class="hidden" accept=".png,.jpg,.jpeg,.gif,.webp,.ico" @change="setPicture" />
            <p v-if="imageTypeError" class="text-xs text-[#EE3939] mt-3">{{ imageTypeError }}</p>
          </div>

          <div class="flex gap-3 px-6 py-4 bg-gray-50 dark:bg-[#161b22] border-t border-gray-100 dark:border-gray-700">
            <button @click="closeModal"
              class="flex-1 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors">
              Cancel
            </button>
            <button @click="confirmUpload"
              :disabled="!pendingChange"
              class="flex-1 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#0058C7] disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center justify-center gap-2">
              <svg v-if="uploading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              {{ uploading ? 'Saving...' : 'Save' }}
            </button>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import axios from 'redaxios'
import ImageCropper from 'controllers/vue_components/settings/shared/ImageCropper.vue'

const props = defineProps({
  pictureUrl: { type: String, default: '' },
  avatarName: { type: String, default: '' },
  avatarColorClass: { type: String, default: '' },
  uploadUrl: { type: String, required: true },
  removeUrl: { type: String, required: true },
  formFieldName: { type: String, required: true },
  title: { type: String, default: 'Change picture' },
  locked: { type: Boolean, default: false },
  lockedMessage: { type: String, default: '' },
  shape: { type: String, default: 'circle', validator: (v) => ['circle', 'rounded'].includes(v) },
  size: { type: String, default: 'w-20 h-20' },
  avatarTextSize: { type: String, default: 'text-2xl' },
  // Optional: a system-provided default picture (e.g. a catalog-matched app
  // logo) the user can explicitly switch back to. Omitted entirely by
  // callers (User/Organization profile pictures) that have no such default.
  defaultPictureUrl: { type: String, default: '' },
  resetUrl: { type: String, default: '' }
})

const emit = defineEmits(['updated', 'error'])

const shapeClass = computed(() => (props.shape === 'circle' ? 'rounded-full' : 'rounded-xl'))

const uploadInputId = `avatar-upload-${Math.random().toString(36).slice(2, 9)}`

const showModal = ref(false)
const showCameraIcon = ref(false)
const uploading = ref(false)
const imageTypeError = ref('')
const tempPreview = ref('')
const cropping = ref(false)
const usingDefault = ref(false)
const selectedFileUrl = ref('')
const fileInputEl = ref(null)
const cropperRef = ref(null)

const pendingChange = computed(() => cropping.value || tempPreview.value !== props.pictureUrl)

function revokeSelectedFileUrl() {
  if (selectedFileUrl.value) URL.revokeObjectURL(selectedFileUrl.value)
  selectedFileUrl.value = ''
}

function openModal() {
  tempPreview.value = props.pictureUrl
  cropping.value = false
  usingDefault.value = false
  revokeSelectedFileUrl()
  imageTypeError.value = ''
  showModal.value = true
}

function closeModal() {
  cropping.value = false
  revokeSelectedFileUrl()
  showModal.value = false
}

function removePicture() {
  cropping.value = false
  usingDefault.value = false
  revokeSelectedFileUrl()
  tempPreview.value = ''
}

function useDefaultPicture() {
  cropping.value = false
  usingDefault.value = true
  revokeSelectedFileUrl()
  imageTypeError.value = ''
  tempPreview.value = props.defaultPictureUrl
}

function triggerFileInput() {
  fileInputEl.value?.click()
}

function setPicture(event) {
  imageTypeError.value = ''
  const file = event.target.files[0]
  event.target.value = ''
  if (!file) return

  const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif|\.webp|\.ico)$/i
  if (!allowedExtensions.exec(file.name)) {
    imageTypeError.value = 'File must be a JPG, PNG, GIF, WEBP, or ICO.'
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    imageTypeError.value = 'File must be under 5 MB.'
    return
  }

  revokeSelectedFileUrl()
  selectedFileUrl.value = URL.createObjectURL(file)
  cropping.value = true
  usingDefault.value = false
}

defineExpose({ openModal })

async function confirmUpload() {
  uploading.value = true
  try {
    if (cropping.value) {
      const blob = await cropperRef.value?.getCroppedBlob()
      if (!blob) {
        emit('error', 'Could not process that image. Please try another.')
        return
      }
      const formData = new FormData()
      formData.append(props.formFieldName, blob, 'avatar.jpg')
      const res = await axios.put(props.uploadUrl, formData)
      if (res.data?.success) {
        emit('updated', res.data.data)
        closeModal()
      } else {
        emit('error', res.data?.message || 'Could not upload picture.')
      }
    } else if (usingDefault.value) {
      const res = await axios.post(props.resetUrl)
      if (res.data?.success) {
        emit('updated', res.data.data)
        closeModal()
      } else {
        emit('error', res.data?.message || 'Could not switch to the default icon.')
      }
    } else if (tempPreview.value === '') {
      const res = await axios.delete(props.removeUrl)
      if (res.data?.success) {
        emit('updated', res.data.data)
        closeModal()
      } else {
        emit('error', res.data?.message || 'Could not remove picture.')
      }
    }
  } catch {
    emit('error', 'Something went wrong. Please try again.')
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
